/**
 * FlowNodeDetails — the system selected in a flow diagram: the status it ended
 * on, time spent, then its statuses as a vertical flow, each edge carrying the
 * gap from the step above. Drawn on the diagram's dark surface, so this is
 * plain markup rather than a Cloudscape Table, which is light-themed.
 */
import { FlowNode, FlowTone } from 'src/store/slices/paymentDetail/paymentDetailTypes';

const GLYPHS: Record<FlowTone, string> = { success: '✓', failed: '✕', neutral: '•' };

interface FlowNodeDetailsProps {
  node: FlowNode;
  onClose: () => void;
}

export const FlowNodeDetails = ({ node, onClose }: FlowNodeDetailsProps) => (
  <div className="flow-panel">
    <header className="flow-panel__header">
      <span className="flow-panel__system">{node.system}</span>
      <span className="flow-panel__count">({node.steps.length})</span>
      <button
        type="button"
        className="flow-panel__close"
        onClick={onClose}
        aria-label={`Close ${node.system} detail`}
      >
        ✕
      </button>
    </header>

    <div className="flow-panel__meta">
      {node.lastStatus && (
        <span
          className={`flow-panel__state flow-panel__status flow-panel__tone--${node.tone}`}
          title={[node.lastStatus, node.state].filter(Boolean).join(' · ')}
        >
          <span aria-hidden="true">{GLYPHS[node.tone]}</span>
          {node.lastStatus}
        </span>
      )}
      {node.latency && <span className="flow-panel__latency">{node.latency} in system</span>}
      {node.bottleneck && <span className="flow-chip flow-chip--warning">Slowest</span>}
    </div>

    {node.passThrough ? (
      <p className="flow-panel__empty">No reported phases — the payment passed straight through.</p>
    ) : (
      <ol className="flow-steps">
        {node.steps.map((step, index) => (
          <li key={step.id}>
            {/* The gap is time between two steps, so it rides the edge. Two
                lines put it mid-edge, centred on the steps it joins. */}
            {index > 0 && (
              <div className="flow-steps__link">
                <span className="flow-steps__line" aria-hidden="true" />
                <span className="flow-panel__gap">{step.gapFromPrevious ?? '—'}</span>
                <span className="flow-steps__line flow-steps__line--arrow" aria-hidden="true" />
              </div>
            )}
            <div className="flow-steps__step">
              <span
                className={`flow-panel__status flow-panel__tone--${step.tone}`}
                title={step.state}
              >
                <span aria-hidden="true">{GLYPHS[step.tone]}</span>
                {step.code}
              </span>
              {step.countLabel && <span className="flow-panel__badge">{step.countLabel}</span>}
              {step.at && <span className="flow-panel__gap flow-steps__at">{step.at}</span>}
            </div>
          </li>
        ))}
      </ol>
    )}
  </div>
);
